/*
 RiskOS Advanced Prefill (Socure) signup action types.

 The backend exposes server-directed signup actions through
 `GET /v1/users/{user_id}/sign_up` and `POST /v1/users/{user_id}/sign_up/advance`.

 Treat `signup_action` as the source of truth. Do not evaluate Statsig or
 client-side flags to enter the RiskOS flow; only the backend can do that.
*/

export type RiskosFlowVariant = 'riskos_advanced_prefill';

export type SignupActionKind =
    | 'identity_prefill'
    | 'collect_phone'
    | 'submit_profile'
    | 'submit_ssn'
    | 'digital_kyc'
    | 'document_kyc'
    | 'manual_review'
    | 'done'
    | 'rejected';

export type RiskosRequiredInput =
    | 'phone_number'
    | 'last4_ssn'
    | 'socure_device_session_id'
    | 'otp_code'
    | 'first_name'
    | 'last_name'
    | 'birth_date'
    | 'address'
    | 'ssn';

// See acceptance criteria. We keep this as an open string union to tolerate
// future backend additions without runtime breakage; never display raw values
// to users — use safe signup copy instead.
export type RiskosFallbackReason =
    | 'provider_error'
    | 'invalid_phone'
    | 'duplicate_phone'
    | 'no_prefill_found'
    | 'high_risk_phone'
    | 'not_displayable'
    | 'otp_failed'
    | 'otp_expired'
    | 'prefill_in_progress'
    | 'previous_attempt_incomplete'
    | 'ssn_not_returned'
    | 'ssn_not_usable'
    | 'no_displayable_fields'
    | 'prefill_disabled'
    | 'fallback'
    | (string & {});

export interface RiskosPrefillAddress {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    is_latest?: boolean;
}

export interface RiskosPrefillSuggestion {
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    suffix?: string;
    birth_date?: string;
    addresses?: RiskosPrefillAddress[];
}

export interface SignupAction {
    kind: SignupActionKind | (string & {});
    flow_variant?: RiskosFlowVariant | (string & {});
    required_inputs?: RiskosRequiredInput[];
    prefill_attempt_id?: string;
    prefill_suggestion?: RiskosPrefillSuggestion;
    fallback_reason?: RiskosFallbackReason;
    expires_at?: string;
    copy_key?: string;
}

export interface RiskosStartInput {
    phone_number: string;
    socure_device_session_id: string;
}

export interface RiskosSsn4Input {
    prefill_attempt_id: string;
    last4_ssn: string;
}

export interface RiskosOtpSubmitInput {
    prefill_attempt_id: string;
    otp_code: string;
}

export interface RiskosOtpResendInput {
    prefill_attempt_id: string;
    resend_otp: true;
}

export type RiskosOtpInput = RiskosOtpSubmitInput | RiskosOtpResendInput;

export type SignUpAdvanceRequest =
    | {
          kind: 'identity_prefill';
          flow_variant: RiskosFlowVariant;
          input: RiskosStartInput | RiskosSsn4Input | RiskosOtpInput;
      }
    // Reserved for future kinds. Keeping the union open lets new server actions
    // be wired without bumping the request type.
    | {
          kind: Exclude<SignupActionKind, 'identity_prefill'>;
          flow_variant?: string;
          input?: Record<string, unknown>;
      };

/**
 * Returns the address the user should review/edit. Prefers the address marked
 * `is_latest`; falls back to the first address if none is flagged.
 */
export function selectLatestPrefillAddress(
    suggestion: RiskosPrefillSuggestion | undefined,
): RiskosPrefillAddress | undefined {
    const addresses = suggestion?.addresses;
    if (!addresses || addresses.length === 0) {
        return undefined;
    }
    return addresses.find((a) => a.is_latest) ?? addresses[0];
}

// Note: these intentionally return plain `boolean` rather than a type
// predicate (`action is SignupAction`). A predicate would cause TypeScript to
// narrow `action` to `undefined` after a sequence of falsy checks against the
// same value (since the predicate asserts "is SignupAction", its negation is
// "is not SignupAction" → only `undefined` left in the union). Callers that
// need the narrowed shape can use optional chaining.
export function isRiskosIdentityPrefillStart(action: SignupAction | undefined): boolean {
    if (!action) return false;
    if (action.kind !== 'identity_prefill') return false;
    if (action.flow_variant !== 'riskos_advanced_prefill') return false;
    const inputs = action.required_inputs ?? [];
    return inputs.includes('phone_number') && inputs.includes('socure_device_session_id');
}

export function isRiskosIdentityPrefillSsn4(action: SignupAction | undefined): boolean {
    if (!action) return false;
    if (action.kind !== 'identity_prefill') return false;
    if (action.flow_variant !== 'riskos_advanced_prefill') return false;
    return (action.required_inputs ?? []).includes('last4_ssn');
}

export function isRiskosIdentityPrefillOtp(action: SignupAction | undefined): boolean {
    if (!action) return false;
    if (action.kind !== 'identity_prefill') return false;
    if (action.flow_variant !== 'riskos_advanced_prefill') return false;
    return (action.required_inputs ?? []).includes('otp_code');
}

export function isRiskosProfileReview(action: SignupAction | undefined): boolean {
    if (!action) return false;
    if (action.kind !== 'submit_profile') return false;
    if (action.flow_variant !== 'riskos_advanced_prefill') return false;
    return !!action.prefill_suggestion;
}
